import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { Comment, UiComment } from '../../models/comment/comment.model';
import { COMMENT_DATA_SERVICE, CommentDataService } from '../../services/comment/comment-data.service';
import { LikesDialogComponent, LikesDialogData } from '../likes-dialog/likes-dialog.component';

@Component({
  selector: 'ptl-comment-item',
  templateUrl: './comment-item.component.html',
  styleUrls: ['./comment-item.component.scss'],
})
export class CommentItemComponent implements OnInit {
  @Input() comment!: UiComment;
  @Input() momentId!: string;
  @Input() isTopLevel = false;
  @Input() currentUserId!: string;
  @Input() isFirstLevelReply = false;

  @Output() commentUpdated = new EventEmitter<Comment>();
  @Output() commentDeleted = new EventEmitter<string>();
  @Output() commentLiked = new EventEmitter<{ commentId: string; isLiked: boolean }>();
  @Output() replyCreated = new EventEmitter<{ reply: Comment; parentCommentId: string }>();

  isCurrentUserAuthor = false;
  isLoading = false;
  isLoadingReplies = false;
  repliesPage = 0;
  repliesPageSize = 5;
  hasMoreReplies = false;

  constructor(
    @Inject(COMMENT_DATA_SERVICE) private commentService: CommentDataService,
    public translocoService: TranslocoService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.isCurrentUserAuthor = this.currentUserId === this.comment.authorId;
  }

  onLikeToggle(): void {
    if (this.isLoading) return;

    this.isLoading = true;
    const isLiked = !this.comment.likes.liked;

    const request$ = isLiked ? this.commentService.likeComment(this.comment.id) : this.commentService.unlikeComment(this.comment.id);

    request$.subscribe({
      next: (result) => {
        if (result.isSuccess) {
          this.commentLiked.emit({ commentId: this.comment.id, isLiked });
        } else {
          console.error('Error toggling like:', result.error);
        }
      },
      error: (error) => {
        console.error('Error toggling like:', error);
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  startEdit(): void {
    this.comment.isEditing = true;
  }

  cancelEdit(): void {
    this.comment.isEditing = false;
  }

  onDelete(): void {
    if (this.isLoading) return;

    this.commentService.deleteComment(this.comment.id).subscribe({
      next: (result) => {
        if (result.isSuccess) {
          this.commentDeleted.emit(this.comment.id);
        } else {
          console.error('Error deleting comment:', result.error);
        }
      },
      error: (error) => {
        console.error('Error deleting comment:', error);
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  startReply(): void {
    this.comment.isReplying = true;
  }

  cancelReply(): void {
    this.comment.isReplying = false;
  }

  onReplyCreated(comment: Comment): void {
    this.comment.isReplying = false;
    this.replyCreated.emit({ reply: comment, parentCommentId: this.comment.id });
  }

  onEditSaved(content: string): void {
    if (content.trim() === this.comment.content) {
      this.cancelEdit();
      return;
    }

    this.isLoading = true;
    this.commentService.updateComment(this.comment.id, { content }).subscribe({
      next: (result) => {
        if (result.isSuccess) {
          this.comment.content = result.value.content;
          this.comment.updatedAt = result.value.updatedAt;
          this.comment.isEditing = false;
          this.commentUpdated.emit(result.value);
        } else {
          console.error('Error updating comment:', result.error);
        }
      },
      error: (error) => {
        console.error('Error updating comment:', error);
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  showLikesDialog(event: Event): void {
    event.stopPropagation();

    if (this.comment.likes.totalLikes > 0) {
      this.commentService.getCommentLikes(this.comment.id).subscribe({
        next: (result) => {
          if (result.isSuccess) {
            const dialogData: LikesDialogData = {
              commentContent: this.comment.content,
              likedBy: result.value,
              totalLikes: this.comment.likes.totalLikes,
            };

            this.dialog.open(LikesDialogComponent, {
              width: '500px',
              maxHeight: '600px',
              data: dialogData,
              panelClass: ['ptl-mat-dialog', 'likes-dialog'],
              backdropClass: 'dialog-backdrop',
            });
          }
        },
        error: (error) => {
          console.error('Error loading comment likes:', error);
        },
      });
    }
  }

  toggleReplies(): void {
    this.comment.isExpanded = !this.comment.isExpanded;

    // Load replies if expanding and they haven't been loaded yet
    if (this.comment.isExpanded && (!this.comment.replies || this.comment.replies.length === 0) && this.comment.repliesCount > 0) {
      this.loadReplies(false);
    }
  }

  loadMoreReplies(): void {
    this.loadReplies(true);
  }

  private loadReplies(loadMore: boolean): void {
    if (this.isLoadingReplies) return;

    this.isLoadingReplies = true;

    if (!loadMore) {
      this.repliesPage = 0;
    } else {
      this.repliesPage++;
    }

    this.commentService.getComments(this.momentId, this.repliesPage, this.repliesPageSize, 'NEWEST', this.comment.id).subscribe({
      next: (result) => {
        if (result.isSuccess) {
          const newReplies = result.value.content.map((reply) => ({
            ...reply,
            isExpanded: false,
            isEditing: false,
            isReplying: false,
            replies: reply.replies
              ? reply.replies.map((nestedReply) => ({
                  ...nestedReply,
                  isExpanded: false,
                  isEditing: false,
                  isReplying: false,
                }))
              : undefined,
          }));

          if (loadMore && this.comment.replies) {
            this.comment.replies = [...this.comment.replies, ...newReplies];
          } else {
            this.comment.replies = newReplies;
          }

          this.hasMoreReplies = result.value.currentPageNumber < result.value.numberOfPages - 1;
        } else {
          console.error('Error loading replies:', result.error);
        }
      },
      error: (error) => {
        console.error('Error loading replies:', error);
      },
      complete: () => {
        this.isLoadingReplies = false;
      },
    });
  }

  trackByCommentId(index: number, comment: UiComment): string {
    return comment.id;
  }
}
